import React, { useRef, useCallback, useState } from 'react';
import { NextUI } from '@/components/NextUI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppDispatch } from '@/hooks/redux';
import { addToast } from '@/store/slices/toastSlice';
import Icon from '@/components/Common/Icons/Icon';
import { useTranslations } from 'next-intl';

const UploadIt = ({
  signRef,
  onSignatureChange,
}: {
  signRef?: React.MutableRefObject<HTMLCanvasElement>;
  onSignatureChange: (hasContent: boolean) => void;
}) => {
  const dispatch = useAppDispatch();
  const t = useTranslations('modals.your_sign');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hasImage, setHasImage] = useState(false);

  React.useEffect(() => {
    onSignatureChange(hasImage);
  }, [hasImage, onSignatureChange]);

  const validateAndProcessFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      dispatch(
        addToast({
          text: t('error_messages.invalid_image'),
          variant: 'danger',
        })
      );
      return false;
    }
    processImage(file);
    return true;
  };

  const processImage = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          if (!canvasRef.current) return;
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d');
          if (!ctx) return;

          const maxWidth = 578;
          const maxHeight = 165;

          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (maxWidth * height) / width;
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = (maxHeight * width) / height;
            height = maxHeight;
          }

          ctx.clearRect(0, 0, canvas.width, canvas.height);

          const x = (maxWidth - width) / 2;
          const y = (maxHeight - height) / 2;

          ctx.drawImage(img, x, y, width, height);

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;

          for (let i = 0; i < data.length; i += 4) {
            const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
            data[i] = avg;
            data[i + 1] = avg;
            data[i + 2] = avg;
          }

          ctx.putImageData(imageData, 0, 0);

          setHasImage(true);

          if (signRef) {
            signRef.current = canvas;
          }
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    },
    [signRef]
  );

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndProcessFile(file);
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) {
        validateAndProcessFile(file);
      }
    },
    [processImage]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleClear = () => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setHasImage(false);
  };

  return (
    <div className="flex flex-col w-full py-4 md:pl-6">
      <div className="flex justify-between w-full">
        <div className="flex gap-4">
          <NextUI.Button
            variant="ghost"
            onClick={handleUploadClick}
            className="text-primary-blue !px-2"
          >
            <FontAwesomeIcon icon={['fas', 'upload']} className="mr-2" />
            {t('buttons.upload_image')}
          </NextUI.Button>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={handleFileChange}
          />
        </div>
      </div>
      <div
        className="bg-primary-extra-light-blue2 h-[233px] w-full rounded-extra-lg mt-5 relative"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <div className="absolute top-0 left-8">
          <Icon name="SignIcon" width={33} height={144} />
        </div>
        <canvas
          ref={canvasRef}
          width={578}
          height={165}
          className="bg-transparent w-full h-full rounded-t-extra-lg"
        />
        {!hasImage && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
            <FontAwesomeIcon
              icon={['fas', 'cloud-upload']}
              className="text-4xl text-gray-medium mb-2"
            />
            <p className="text-gray-medium text-sm">
              {t('placeholders.upload_instructions')}
            </p>
          </div>
        )}
        <div className="px-8 absolute bottom-0 w-full">
          {!hasImage && <div className="border-t border-gray-300" />}

          <div className="flex justify-end mt-4 mb-4">
            <NextUI.Button
              variant="link"
              onClick={handleClear}
              className="!p-0 !px-0 !py-0 text-primary-blue !min-w-0"
            >
              {t('buttons.clear')}
            </NextUI.Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadIt;
